import { TRPC_ERROR_CODE_KEY } from '@trpc/server/rpc';
import { TError } from './errors';

/**
 * @desc What kind of failure ended an agent turn, as the client needs to distinguish it.
 *
 * Deliberately coarser than the tRPC error code: the client's only real decisions are what to say,
 * whether to invite a retry, and how loudly to render it. A code-per-kind mapping keeps those
 * decisions on the server, where the codes are actually known.
 */
export const AgentStreamErrorKind = {
  /** @desc A fair-use allowance is exhausted. Not a fault — the control working. */
  Quota: 'quota',
  /** @desc The caller may not do this: signed out, or lacking the role. */
  Denied: 'denied',
  /** @desc Nothing to act on: the conversation is gone. */
  NotFound: 'not-found',
  /** @desc A dependency is down or shutting down. Worth retrying shortly. */
  Unavailable: 'unavailable',
  /**
   * @desc The model's reply was cut off before it was usable, so the turn produced nothing.
   *
   * Its own kind rather than `Internal`: nothing is broken, the answer simply did not fit, and
   * the remedy is the user's (retry, or ask for less) rather than ours. Rendering it as a fault
   * would be both wrong and unhelpful.
   */
  Truncated: 'truncated',
  /** @desc Anything else — a bug. The message is ours, never the underlying error's. */
  Internal: 'internal',
} as const;

export type AgentStreamErrorKind = (typeof AgentStreamErrorKind)[keyof typeof AgentStreamErrorKind];

/**
 * @desc A turn-ending failure, classified and made safe to display.
 *
 * Carried as a *yielded stream event* rather than thrown, which is the whole point of the type.
 * An exception crossing tRPC's SSE transport passes through `getTRPCErrorFromUnknown` (which
 * flattens any non-`TRPCError` to INTERNAL_SERVER_ERROR), then error-shape serialisation, then an
 * `EventSource` that reports connection failures as bare events — three chances for the detail to
 * be lost, and it was. Ordinary stream data survives all of them intact.
 */
export type AgentStreamError = {
  kind: AgentStreamErrorKind;
  /** @desc Shown to the user verbatim. Never the raw text of an unrecognised error. */
  message: string;
  /** @desc Whether trying the same thing again could plausibly work. Drives the UI's affordance. */
  retryable: boolean;
};

/** @desc Shown for {@link AgentStreamErrorKind.Internal} — nothing derived from the cause. */
const INTERNAL_MESSAGE = 'Something went wrong on our side. Please try again.';

/**
 * @desc Shown for {@link AgentStreamErrorKind.Truncated}.
 *
 * Deliberately says nothing about tokens, ceilings, `max_tokens` or any other model internal:
 * the user cannot act on the mechanism, only on the size of what they asked for. The specifics
 * (stop reason, output tokens, model, turn id) go to the log and the metric, where they can be
 * acted on by us.
 */
export const TRUNCATED_OUTPUT_MESSAGE =
  'The response was cut short and could not be completed. Please try again, or ask for a ' +
  'smaller piece of the analysis.';

/**
 * @desc Shown for a turn the harness stopped because a tool kept failing (`TurnEnded`,
 * `repair_budget_exhausted`). The kit's own sentence names the tool (`python_executor`), which
 * means nothing to the reader; the specifics stay on the event's `tools` and `detail`.
 */
export const TOOL_BREAK_MESSAGE =
  'The analysis kept failing at the same step, so it was stopped. Try rephrasing the request.';

/** @desc Shown for a turn the harness's watchdog stopped for running too long (`TurnEnded`, `watchdog`). */
export const TURN_TIMEOUT_MESSAGE =
  'The response took too long and was stopped. Try breaking the request into smaller steps.';

const KIND_BY_CODE: Partial<Record<TRPC_ERROR_CODE_KEY, AgentStreamErrorKind>> = {
  TOO_MANY_REQUESTS: AgentStreamErrorKind.Quota,
  UNAUTHORIZED: AgentStreamErrorKind.Denied,
  FORBIDDEN: AgentStreamErrorKind.Denied,
  NOT_FOUND: AgentStreamErrorKind.NotFound,
  SERVICE_UNAVAILABLE: AgentStreamErrorKind.Unavailable,
  TIMEOUT: AgentStreamErrorKind.Unavailable,
};

const KINDS: ReadonlySet<AgentStreamErrorKind> = new Set(Object.values(AgentStreamErrorKind));

const RETRYABLE: ReadonlySet<AgentStreamErrorKind> = new Set([
  AgentStreamErrorKind.Unavailable,
  AgentStreamErrorKind.Internal,
  AgentStreamErrorKind.Truncated,
]);

/**
 * @desc Build a displayable turn failure from a kind the server already decided.
 *
 * The counterpart to {@link toAgentStreamError}, for failures that arrive as data rather than as
 * a thrown error — a run error delivered over the conversation socket, whose classification the
 * run side made and the client only renders. Keeping both constructors here is what stops the
 * retryable/not-retryable decision being restated per transport.
 */
export function agentStreamError(kind: unknown, message: string): AgentStreamError {
  // The kind arrives over the wire from a peer that may be a NEWER revision than this client —
  // a rolling deploy is exactly when turns fail — and the renderer indexes a presentation table
  // by it. An unrecognised kind therefore has to become `Internal` here rather than a lookup
  // miss at render time, which would be a blank screen instead of the failure it was reporting.
  const known = KINDS.has(kind as AgentStreamErrorKind)
    ? (kind as AgentStreamErrorKind)
    : AgentStreamErrorKind.Internal;
  return {
    kind: known,
    message: message.trim() || INTERNAL_MESSAGE,
    retryable: RETRYABLE.has(known),
  };
}

const stringCode = (value: unknown): TRPC_ERROR_CODE_KEY | undefined => {
  const code = (value as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' ? (code as TRPC_ERROR_CODE_KEY) : undefined;
};

/**
 * @desc Where the code lives depends on which side of the wire the error comes from: a
 * `TRPCClientError` — what `ChatAgent` actually catches — deserialises it into `data.code` and has
 * no top-level `code` at all, while a server-side `BackendError`/`TRPCError` carries it top-level.
 * Reading only the latter is the bug this survived once: every quota refusal classified as
 * Internal, because the wire shape was never what the classifier assumed.
 */
const codeOf = (err: unknown): TRPC_ERROR_CODE_KEY | undefined => {
  if (typeof err !== 'object' || err === null) return undefined;
  return stringCode((err as { data?: unknown }).data) ?? stringCode(err);
};

/**
 * @desc Classify a thrown error into something the client can render.
 *
 * The message is only taken from the error for kinds the backend writes *for the user* — a quota
 * refusal states its own reset instant, and no generic string could. An unrecognised error is a
 * bug, so its text is replaced outright: it may carry a stack, a query or a connection string, and
 * this value is rendered in the conversation.
 */
export function toAgentStreamError(err: unknown): AgentStreamError {
  const code = codeOf(err);
  const kind = (code && KIND_BY_CODE[code]) || AgentStreamErrorKind.Internal;
  const message =
    kind === AgentStreamErrorKind.Internal
      ? INTERNAL_MESSAGE
      : ((err as Partial<TError.BackendError>).message?.trim() ?? '') || INTERNAL_MESSAGE;

  return { kind, message, retryable: RETRYABLE.has(kind) };
}
